import React, { useCallback, useState } from 'react';

import { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import type { OrgAnalyticsResponse } from '@/pages/analytics/types';
import { COLOR_ATTENTION, COLOR_CRITICAL } from '@/pages/analytics/utils/chartPalette';
import {
  normalizeCallVolumeTrend,
  normalizeComplianceTrend,
  normalizeEscalationTrend,
  normalizeQuestionRates,
  normalizeRadar,
  normalizeScoreTrend,
} from '@/pages/analytics/utils/normalizeChartRows';
import type { OrgChartKey } from '@/pages/analytics/utils/orgChartVisibility';
import { ORG_ANALYTICS_PDF_SECTION } from '@/pages/analytics/utils/orgPdfExportSections';
import { formatIneligibleReasonBucketTitle, type QcDrilldownScope } from '@/pages/qc/utils';

import { CallBreakdownGrid, IneligibleReasonBreakdownSection } from './CallBreakdownGrid';
import { CallVolumeTrendChart } from './CallVolumeTrendChart';
import { ChartCard } from './ChartCard';
import { ChartRenderBoundary } from './ChartRenderBoundary';
import { ChartBodySkeleton } from './ChartStates';
import { OrgKpiGrid } from './OrgKpiGrid';
import { PerformanceRadarChart } from './PerformanceRadarChart';
import { QuestionPassRates } from './QuestionPassRates';
import { RateTrendChart } from './RateTrendChart';
import { type ChartScope, OVERALL_BY_PROGRAM_OPTIONS, ScopeToggle } from './ScopeToggle';
import { ScoreTrendChart } from './ScoreTrendChart';

// Pure presentational view of every analytics metric on a single page.
// Owns the four per-chart scope toggles (Score, Radar, Compliance, Escalation)
// since those are intra-card UI state, but takes everything else as props so
// it can be reused by the Org tab and the Agent detail page.
//
// Filter changes (program, date range, agent) re-render this component by
// flowing through `data` — each child memoizes on its slice so the chart
// state and scope toggles stay stable across re-renders.

interface AnalyticsMetricsViewProps {
  data: OrgAnalyticsResponse | undefined;
  loading: boolean;
  /** Built from `useDownloadChart()` by the parent. */
  download: ReturnType<typeof useDownloadChart>;
  /** Label for the radar's dashed comparison polygon. Defaults to "Prior Period".
   *  Agent detail overrides to "Organization Average" — see PerformanceRadarChart. */
  priorLabel?: string;
  /** Org id (unprefixed) used to build the QC drill-down link when a metadata
   *  breakdown value is clicked. When absent, breakdown rows are non-interactive. */
  organizationId?: string | null;
  /** Agent detail passes the routed agent id so the question deep-dive links scope
   *  to this agent (the id is a path param, not in searchParams). Org tab omits it. */
  agentId?: string | null;
  /** Chart keys to render. When undefined (e.g. Agent detail) every chart is
   *  shown; the Org tab passes the user's "Customize" selection. */
  visibleCharts?: OrgChartKey[];
  /** Analytics filters carried into the call-breakdown QC drill-down. */
  drilldownScope?: QcDrilldownScope;
}

export const AnalyticsMetricsView: React.FC<AnalyticsMetricsViewProps> = ({
  data,
  loading,
  download,
  priorLabel,
  organizationId,
  agentId,
  visibleCharts,
  drilldownScope,
}) => {
  const [scoreScope, setScoreScope] = useState<ChartScope>('overall');
  const [callVolumeScope, setCallVolumeScope] = useState<ChartScope>('overall');
  const [radarScope, setRadarScope] = useState<ChartScope>('overall');
  const [complianceScope, setComplianceScope] = useState<ChartScope>('overall');
  const [escalationScope, setEscalationScope] = useState<ChartScope>('overall');
  const trendBucket = data?.trend_bucket ?? 'day';

  // No selection prop → show everything (Agent detail). Otherwise honor the
  // user's Org-tab "Customize" choices.
  const shows = useCallback(
    (key: OrgChartKey): boolean => !visibleCharts || visibleCharts.includes(key),
    [visibleCharts],
  );
  // Empty (but present) selection = the user hid every chart via "Customize".
  const noChartsVisible = !!visibleCharts && visibleCharts.length === 0;

  return (
    <>
      <div data-export-section={ORG_ANALYTICS_PDF_SECTION.summary}>
        <OrgKpiGrid summary={data?.summary} loading={loading} />
      </div>

      {noChartsVisible && (
        <div className="rounded-md border border-dashed border-border bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
          All charts are hidden. Use “Customize” to choose which charts to display.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-export-grid-group>
        {shows('score_trend') && (
          <ChartCard
            title="Score Trend"
            exportSectionId={ORG_ANALYTICS_PDF_SECTION.scoreTrend}
            info="Average quality score over time. Each point represents the mean AI score for all calls in that period. Reference lines show score band thresholds: Exemplary (98+) and Meets (95+); scores below 95 are Unsatisfactory."
            rightSlot={
              <ScopeToggle
                value={scoreScope}
                onChange={setScoreScope}
                options={OVERALL_BY_PROGRAM_OPTIONS}
              />
            }
            onDownload={(format, exportElement) =>
              data &&
              download(
                normalizeScoreTrend(data.score_trend, scoreScope),
                format,
                'Score Trend',
                exportElement,
              )
            }
            downloadDisabled={!data}
          >
            {loading ? (
              <ChartBodySkeleton height={280} />
            ) : (
              <ChartRenderBoundary
                resetKeys={[data?.score_trend, scoreScope, trendBucket]}
                height={280}
              >
                <ScoreTrendChart
                  data={data?.score_trend ?? []}
                  scope={scoreScope}
                  trendBucket={trendBucket}
                />
              </ChartRenderBoundary>
            )}
          </ChartCard>
        )}

        {shows('call_volume_trend') && (
          <ChartCard
            title="Call Volume"
            exportSectionId={ORG_ANALYTICS_PDF_SECTION.callVolumeTrend}
            info="Number of calls over time. The granularity (daily, weekly, or monthly) follows the selected date range. Use the toggle to view overall volume or split by program."
            rightSlot={
              <ScopeToggle
                value={callVolumeScope}
                onChange={setCallVolumeScope}
                options={OVERALL_BY_PROGRAM_OPTIONS}
              />
            }
            onDownload={(format, exportElement) =>
              data &&
              download(
                normalizeCallVolumeTrend(data.call_volume_trend, callVolumeScope),
                format,
                'Call Volume',
                exportElement,
              )
            }
            downloadDisabled={!data}
          >
            {loading ? (
              <ChartBodySkeleton height={280} />
            ) : (
              <ChartRenderBoundary
                resetKeys={[data?.call_volume_trend, callVolumeScope, trendBucket]}
                height={280}
              >
                <CallVolumeTrendChart
                  data={data?.call_volume_trend ?? []}
                  scope={callVolumeScope}
                  trendBucket={trendBucket}
                />
              </ChartRenderBoundary>
            )}
          </ChartCard>
        )}

        {shows('performance_radar') && (
          <ChartCard
            title="Performance Radar"
            exportSectionId={ORG_ANALYTICS_PDF_SECTION.performanceRadar}
            info="Radar chart comparing average scores across the three rubric sections (Compliance, Communication, Content). The solid area represents the current period and the dashed outline shows the prior period for trend comparison."
            rightSlot={
              <ScopeToggle
                value={radarScope}
                onChange={setRadarScope}
                options={OVERALL_BY_PROGRAM_OPTIONS}
              />
            }
            onDownload={(format, exportElement) =>
              data &&
              download(
                normalizeRadar(data.performance_radar, radarScope),
                format,
                'Performance Radar',
                exportElement,
              )
            }
            downloadDisabled={!data}
          >
            {loading ? (
              <ChartBodySkeleton height={280} />
            ) : (
              <ChartRenderBoundary
                resetKeys={[data?.performance_radar, radarScope, priorLabel]}
                height={280}
              >
                <PerformanceRadarChart
                  data={data?.performance_radar}
                  scope={radarScope}
                  priorLabel={priorLabel}
                />
              </ChartRenderBoundary>
            )}
          </ChartCard>
        )}

        {shows('compliance_failure_rate') && (
          <ChartCard
            title="Compliance Failure Rate"
            exportSectionId={ORG_ANALYTICS_PDF_SECTION.complianceFailureRate}
            info="Weekly percentage of calls with at least one critical compliance question failure. The threshold line marks the target maximum rate."
            rightSlot={
              <ScopeToggle
                value={complianceScope}
                onChange={setComplianceScope}
                options={OVERALL_BY_PROGRAM_OPTIONS}
              />
            }
            onDownload={(format, exportElement) =>
              data &&
              download(
                normalizeComplianceTrend(data.compliance_trend, complianceScope),
                format,
                'Compliance Failure Rate',
                exportElement,
              )
            }
            downloadDisabled={!data}
          >
            {loading ? (
              <ChartBodySkeleton />
            ) : (
              <ChartRenderBoundary
                resetKeys={[data?.compliance_trend, complianceScope, trendBucket]}
              >
                <RateTrendChart
                  data={data?.compliance_trend ?? []}
                  scope={complianceScope}
                  color={COLOR_CRITICAL}
                  trendBucket={trendBucket}
                  seriesLabel="Compliance Failure Rate"
                />
              </ChartRenderBoundary>
            )}
          </ChartCard>
        )}

        {shows('escalation_rate') && (
          <ChartCard
            title="Escalation Rate"
            exportSectionId={ORG_ANALYTICS_PDF_SECTION.escalationRate}
            info="Weekly percentage of calls flagged as requiring escalation or supervisor review."
            rightSlot={
              <ScopeToggle
                value={escalationScope}
                onChange={setEscalationScope}
                options={OVERALL_BY_PROGRAM_OPTIONS}
              />
            }
            onDownload={(format, exportElement) =>
              data &&
              download(
                normalizeEscalationTrend(data.escalation_trend, escalationScope),
                format,
                'Escalation Rate',
                exportElement,
              )
            }
            downloadDisabled={!data}
          >
            {loading ? (
              <ChartBodySkeleton />
            ) : (
              <ChartRenderBoundary
                resetKeys={[data?.escalation_trend, escalationScope, trendBucket]}
              >
                <RateTrendChart
                  data={data?.escalation_trend ?? []}
                  scope={escalationScope}
                  color={COLOR_ATTENTION}
                  yMax={0.15}
                  trendBucket={trendBucket}
                  seriesLabel="Escalation Rate"
                />
              </ChartRenderBoundary>
            )}
          </ChartCard>
        )}
      </div>

      {shows('question_rates') && (
        <QuestionPassRates
          data={data?.question_rates ?? []}
          loading={loading}
          organizationId={organizationId}
          agentId={agentId}
          exportSectionId={ORG_ANALYTICS_PDF_SECTION.questionRates}
          onDownload={(format, exportElement) =>
            data &&
            download(
              normalizeQuestionRates(data.question_rates),
              format,
              'Question Pass & Miss Rates',
              exportElement,
            )
          }
        />
      )}

      {shows('ineligible_reason_breakdown') &&
        (loading || (data?.ineligible_reason_breakdown?.reasons?.length ?? 0) > 0) && (
          <IneligibleReasonBreakdownSection
            data={data?.ineligible_reason_breakdown}
            totalCalls={data?.summary?.total_calls?.value}
            loading={loading}
            onDownload={(format, exportElement) =>
              data?.ineligible_reason_breakdown &&
              download(
                {
                  filenameStem: 'why_calls_werent_scored',
                  columns: ['Reason', 'Calls'],
                  rows: data.ineligible_reason_breakdown.reasons.map(reason => ({
                    Reason: formatIneligibleReasonBucketTitle(reason.reason_label),
                    Calls: reason.count,
                  })),
                },
                format,
                "Why calls weren't scored",
                exportElement,
              )
            }
          />
        )}

      {shows('call_analytics') && (loading || data?.call_breakdown) && (
        <CallBreakdownGrid
          data={data?.call_breakdown}
          loading={loading}
          evaluatedCalls={data?.summary?.evaluated_calls?.value}
          exportSectionId={ORG_ANALYTICS_PDF_SECTION.callAnalytics}
          organizationId={organizationId}
          drilldownScope={drilldownScope}
        />
      )}
    </>
  );
};
